import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { cacheService, searchService, userService } from "../../Core/Api/services";
import { LoginRequest } from "../../Core/Api/Types/login-request";
import LogLayout from "../../Layouts/Log";
import { routePaths } from "../../Router/router";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { useToggle } from "../../Utils/Hooks/useToggle";
import { useDispatch } from "react-redux";
import { InitState } from "../../Redux/Reducers/redux-slice-asset";
import { SearchRequestNative } from "../../Core/Api/Types/search-request-native";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { UserHasAcl } from "../../Utils/helpers";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { PlatformMode } from "../../Core/Api/Types/platform-mode";
import { useTranslation } from "react-i18next";

const LoginByKey = () => {
  const { t } = useTranslation();
  const { active: visiblePassword, toggle: togglePasswordVisibility } = useToggle(false);
  const { register, handleSubmit } = useForm<LoginRequest>();
  const key: string = useParams().key!;
  const isNewUser: boolean = useSearchParams()[0].get("new") !== null;
  const navigate = useNavigate();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const onSubmit = handleSubmit(({ login, password }) => {});
  const dispatch = useDispatch();

  useEffect(() => {
    userService
      .LoginByKey(key)
      .then((response) => {
        if (isNewUser) navigate(routePaths.newUser.path);
        else
          userService.SetDataOnLoginSuccess().then(async function () {
            dispatch(InitState());
            // Password expired, route to new password creation
            if (response.rescode === -10) {
              navigate(routePaths.loginNewPassword.path);
              enqueueCustomSnackbar(t("alert:warning.lastPasswordIsTooOld"), "warning");
              return;
            }

            // Fully ok
            if (response.rescode === 0) {
              if (response.group?.acls?.includes(AclTypes.aclLockEverything)) {
                navigate(routePaths.lockedUser.path);
                return;
              }
              if (response.group?.acls?.includes(AclTypes.aclRestrictViewBundleOnly)) {
                navigate(routePaths.inventoryBundlesDashboard.path);
                return;
              }
              if (
                cacheService.IsDriveEnabled() &&
                (UserHasAcl(AclTypes.aclDrive) || cacheService.GetPlatformMode() === PlatformMode.Browser) &&
                response.platformHeadObjectId
              ) {
                cacheService.SetPlatformDriveHeadId(response.platformHeadObjectId);
                navigate(routePaths.home.path);
              } else navigate(routePaths.home.path);
            } else {
              enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
            }
          });
      })
      .catch((message) => {
        enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message }), "warning");
        navigate(routePaths.login.path);
      });
  }, [key]);

  return (
    <LogLayout sxRightContainer={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <Stack alignItems="center">
        <Typography variant="h3" textAlign="center">
          {t("page:body.pleaseLoginToContinue")}
        </Typography>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <form onSubmit={onSubmit}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink htmlFor="email" sx={{ flex: 1 }}>
            {t("common:login")}*
          </InputLabel>
          <OutlinedInput disabled size="medium" defaultValue={"Authentification"} />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 5 }}>
          <InputLabel shrink htmlFor="password" sx={{ flex: 1 }}>
            {t("common:password")}*
          </InputLabel>
          <OutlinedInput
            {...register("password")}
            type={visiblePassword ? "text" : "password"}
            autoComplete="password"
            defaultValue={"please Wait"}
            required
            id="password"
            disabled
            endAdornment={
              <IconButton sx={{ mr: -1 }}>{visiblePassword ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton>
            }
          />
        </FormControl>
        <Stack alignItems="center">
          <Stack
            flexWrap={"nowrap"}
            flexDirection="row"
            justifyContent={"space-around"}
            width="100%"
            alignItems={"center"}
          >
            <Button type="submit" variant="contained" fullWidth size="large">
              <CircularProgress color="secondary" />
            </Button>
          </Stack>
          <Link
            underline="hover"
            component={RouterLink}
            to={routePaths.loginForgotPasswordRequest.path}
            sx={{ mt: 4, color: "rgba(255, 255, 255, 0.7)" }}
          >
            {t("button:forgotYourPassword")}
          </Link>
        </Stack>
      </form>
    </LogLayout>
  );
};

export default LoginByKey;
